// 覚醒王の致命「灰を食む」の比較版 (2026-10-09 夜・DEV の URL スイッチだけ)。既定 (配布版) は跳び噛み (下の AwokenDevourMode の注記・awoken_devour_pounce.ts)。
//
// ユーザー原文 (10-09):「覚醒王はむさぼり食うような食らいつきをしてほしい。予備動作の段階でも攻撃しているように見えて、
//   致命の一撃がどれかわかりにくい作りにしたい。」→ 夜の答え:「読みにくくする」作りは覚醒王だけ・夜は比較版まで。
// 設計 = docs/research/2026-10-09-awoken-devour-execution-research.md の「4. 案A」。
//   崩れた主人公の手前の床を 2〜4 回 (重み 1:2:1) むさぼりながら一口ごとに寄る (見せの噛み・当たりなし・紫なし)。
//   最後に後ろ脚を沈め直し (extend_coil = 暴連噛・伸びと同じ合図)、起き上がり (rush_windup)、立ち上がって口を開けて止まる (exec_windup)。
//   紫のオーラと一瞬の止まりは、この本命の溜めの 200ms 目で初めて出る。最後の 120ms は完全に止まり、
//   前脚で床を打って飛び込み (110ms)、噛む (= 今の暴噛・終と同じ当たり・同じ口の位置・弾いた後も同じ)。
//
// 作り: 致命に入る時に「この回の段取り」(噛みの回数・1回ずつの長さ・寄る距離) を決めて、技の定義の写しに持たせる。
//   id は今の 'kingExecutionAwoken' のまま (口の当たり・口の着地・弾きの仕組みはそのまま効く)。溜めの長さだけ毎回違う。
//   絵の並び (タイムライン) と体の位置は、その段取りから毎回作る。新しい絵は描いていない (今ある絵の並べ替えだけ)。
// 主人公が近すぎる時 (崩れた時の間合いは 110〜180px がほとんど) は、まず後ろ跳び (配布版の後ろ跳びの絵) で間を取ってから食む。
//   見せの噛みが主人公の足に届いて見えないように (最後の見せの噛みの前端 = 足先の 35px 手前)。
import type { AttackDef } from './types'
import type { BossAttackTimeline, BossPoseKey } from './boss_pose_timeline'
import { PLAYER } from './balance'
import { execParryEnabled } from './exec_parry'
import { AWOKEN_BITE_REST_FORWARD_PX } from './awoken_bite_mouth'
import { awokenPounceGapAt, awokenPounceTimeline, planAwokenPounce, pounceActiveAura, pounceAuraK, pounceSignalFreezes, type AwokenPounceExtra } from './awoken_devour_pounce'

// 'pounce' = 10-09 夜の2つ目の比較版 (URL は ?awokenDevour=gladius・跳び噛み2回 → 3回目が本命・src/awoken_devour_pounce.ts)。
//   URL の値の綴りは配布版に出さない (scripts/check_dev_leak.mjs)。中の名前は 'pounce'。
// 2026-10-09 夜 ユーザー (窓口経由)「この形で進める」(跳び噛みで進める・前の版には戻さない)・合図 =「紫が灯る」:
//   致命を弾ける仕組み (src/exec_parry.ts) が効いている時の既定 = 跳び噛み (?awokenDevour の指定なし)。
//   2026-10-10 に弾ける仕組みを配布版の既定にした (答えるページ exec-parry-ship「入れる」) ので、配布版の覚醒王の致命も跳び噛み。
//   弾ける仕組みを切った時 (DEV ?execParry=off) だけ暴噛・終。跳び噛みは「崩れが致命まで続く・崩れ中にパリィを受け付ける」
//   が前提で、それ無しでは崩れ 1.8 秒が致命の溜め (1.2〜1.75 秒＋寄り) より先に解けることがあるため。
//   前の版を見る時は開発版で ?awokenDevour=off (暴噛・終) / 1|2|3|4 (灰を食む)。
export type AwokenDevourMode = 'off' | 'random' | 2 | 3 | 4 | 'pounce'
let explicit: AwokenDevourMode | null = null

/** URL の値から切り替える ('1' = 回数は毎回くじ・'2'|'3'|'4' = 回数を固定・'gladius' = 跳び噛み・'off' = 暴噛・終・
 *  それ以外と指定なし = 既定 (致命を弾ける版なら跳び噛み・そうでなければ暴噛・終)。返り値 = いま効いている版。 */
export function setAwokenDevour(value: string | null | undefined): AwokenDevourMode {
  explicit = value === '1' ? 'random' : value === '2' ? 2 : value === '3' ? 3 : value === '4' ? 4
    : value === 'gladius' ? 'pounce' : value === 'off' ? 'off' : null
  return awokenDevourMode()
}
export function awokenDevourMode(): AwokenDevourMode { return explicit ?? (execParryEnabled() ? 'pounce' : 'off') }
export function awokenDevourEnabled(): boolean { return awokenDevourMode() !== 'off' }

// ?awokenDevour=1&devourJaw=1 (10-09 夜・もう1段のスイッチ): 見せの噛みの「顎を閉じる」拍を、
//   床に頭を突っ込んで顎を閉じた新しい絵 (floorbite_closed_1009) に差し替える。無ければ今の比較版のまま (frenzy_bite2 を縦に潰して見せる)。
//   ?awokenDevour が無い時は効かない (比較版そのものが切れているので)。
let jaw = false
/** URL の値から切り替える ('1' = 顎を閉じた絵を使う・それ以外 = 今の比較版のまま)。 */
export function setAwokenDevourJaw(value: string | null | undefined): boolean {
  jaw = value === '1'
  return jaw
}
export function awokenDevourJawEnabled(): boolean { const m = awokenDevourMode(); return jaw && m !== 'off' && m !== 'pounce' }
/** 顎を閉じた絵の名前 (public/sprites/bosses/crowned-king-awoken/floorbite_closed_1009.webp|png)。 */
export const AWOKEN_DEVOUR_JAW_IMAGE = 'floorbite_closed_1009'

export const AWOKEN_DEVOUR_BASE_ID = 'kingExecutionAwoken'

export const AWOKEN_DEVOUR = {
  /** 見せの噛みの回数の重み (2回:3回:4回)。 */
  biteWeights: { 2: 1, 3: 2, 4: 1 } as Record<2 | 3 | 4, number>,
  /** 見せの噛み 1 回の長さと揺らし (1 回ごとに ±jitter・一定の拍にしない)。 */
  biteMs: 300,
  biteJitterMs: 40,
  /** 1 回の中の割り振り (合計 300): 前足を一歩出して這い出る / 頭を下ろす / 顎を閉じる / 噛みしめて後ろ足を引き寄せる。
   *  10-09 夜の3回目: 寄り方を2段の接地にした (足を床の上で滑らせない)。
   *    這い出る = 後ろ足を床に固定したまま体を前へ伸ばす (前足だけが一口ぶん前へ出る)。
   *    頭を下ろす〜顎を閉じる = 前足も後ろ足も動かない。
   *    噛みしめ = 前足を固定したまま、後ろ足側を rearLiftPx だけ浮かせて一口ぶん引き寄せる (体の伸びもここで戻す)。 */
  biteSplit: { reach: 100, down: 70, close: 50, up: 80 },
  /** 這い出る間に前足を浮かせる高さ (後ろ足は床に着いたまま・体を少し後ろへ傾けて浮かせる)・噛みしめの間に後ろ足を浮かせる高さ
   *  (前足は床に着いたまま・少し前へ傾けて浮かせる)。着いている足は床の上で動かさず、寄せる足は浮いている間だけ動く。 */
  frontLiftPx: 4,
  rearLiftPx: 4,
  /** 構え (肩を落として頭を下げる)。 */
  stanceMs: 250,
  /** 本命の溜め: 後ろ脚の沈み直し → 起き上がり → (200ms 目) 立ち上がり切って口を開けて止まる 360 (うち最後の 120 は完全に止まる)。
   *  10-09 夜の2回目: 沈み直し (extend_coil) から立ち上がり (exec_windup) へ1コマで切り替わると頭が約 70px 跳んでいた。
   *  間に「起き上がる途中」の絵 (rush_windup = 前脚を床に着いたまま胸と頭を起こした絵) を挟む。紫と一瞬の止まりは
   *  立ち上がり切った所 (= 200ms 目・今のまま)。 */
  coilMs: 200,
  /** 沈み直しの一番低い所 (本命の溜めの頭から)・起き上がる途中の絵へ替わる時刻・起き上がり切る時刻。 */
  coilLowMs: 70,
  riseStartMs: 120,
  riseFullMs: 175,
  openMs: 360,
  stillMs: 120,
  /** 本命の飛び込み (前脚で床を打って飛び込み、噛む)。この後が当たり (active の頭 = 今の暴噛・終と同じ)。 */
  lungeMs: 110,
  lungeHopPx: 12,
  /** 噛むたびに寄る距離 (回数ごと・目安)。間合いが足りない時は縮む (下限 8)。
   *  10-09 夜の2回目: 20 → 35〜45 (一口ごとに寄るのが見えるように)。回数が多いほど 1 回を短くして、後ろ跳びの距離を抑える。 */
  stepPxByBites: { 2: 45, 3: 40, 4: 35 } as Record<2 | 3 | 4, number>,
  stepMinPx: 8,
  /** 最後の見せの噛みの前端 = 主人公の足先 (当たりの半幅) からこれだけ手前。 */
  toeGapPx: 35,
  /** 床を噛んでいる間の絵の前端 = 一番前の接地した爪 (前足は這い出た所に固定)。最後の一口の後の立ち姿 (bite_windup_deep・横 1.02 倍・前へ -6px)
   *  の前足と同じ所 = ボスの中心から前へ 1.02 × 147.159 − 6 = 144.1px (表示 186px)。frenzy_bite2 は顎の先と爪がほぼ同じ所 (実測)。 */
  floorPoseFrontPx: 1.02 * 147.159 - 6,
  /** ?devourJaw=1 の顎を閉じた絵 (floorbite_closed_1009) は、顎と角の先が前足の爪より前へ 3.24px (横の倍率 1) 出る。床を噛む時の横の倍率
   *  (約 1.11) を掛けた 3.6px を、足先との間を同じ 35px に保つため最後の見せの噛みの間合いへ足す。 */
  floorPoseJawExtraFrontPx: 3.244 * 1.11,
  /** 後ろ跳び (配布版の後ろ跳びと同じ拍: 屈み 90 → 宙 230 → 着地 110・高さ 28)。
   *  跳ぶ距離が長い時だけ宙を延ばし、弧も少し高くする (110px で 230ms・上限 340ms／150px まで高さ 28・上限 38)。
   *  10-09 夜の2回目: 一口の寄りを 35〜45px にしたので、近い時に跳ぶ距離が 190〜240px まで延びる (上限を 300 → 340ms へ)。
   *  これより近ければ (minPx 未満) 跳ばずに噛みの寄りを縮める。 */
  hop: { crouchMs: 90, airMs: 230, landMs: 110, heightPx: 28, heightRefPx: 150, maxHeightPx: 38, minPx: 20, refPx: 110, maxAirMs: 340 },
  /** 遠い時は構えの間に寄る (今の致命と同じく滑って寄る)。この距離を超えた分 1px あたり 1.5ms 構えを延ばす (上限 +350ms)。 */
  approachFreePx: 30,
  approachMsPerPx: 1.5,
  approachMaxExtraMs: 350,
} as const

export type AwokenDevourPlan = {
  bites: 2 | 3 | 4
  /** 見せの噛み 1 回ずつの長さ (ms)。 */
  biteMs: number[]
  /** 崩れた時の間合い・後ろ跳びの後の間合い・最後の見せの噛みの間合い・噛んだ時の間合い (いずれもボス中心と主人公の差 px)。 */
  startGapPx: number
  biteStartGapPx: number
  lastBiteGapPx: number
  landingGapPx: number
  /** 後ろ跳びの宙の長さ (0 = 跳ばない)。 */
  hopAirMs: number
  stanceMs: number
  /** 区切りの時刻 (溜めの頭から ms)。 */
  biteStartMs: number
  coilStartMs: number
  signalMs: number
  lungeStartMs: number
  chargeMs: number
  /** ?awokenDevour=gladius の版だけ: 跳び噛みの段取り (src/awoken_devour_pounce.ts)。この時 bites = 2 (当たり無しの跳び噛み)・biteMs = 跳び1回ずつの長さ。 */
  pounce?: AwokenPounceExtra
}

function pickBites(rand: () => number): 2 | 3 | 4 {
  const mode = awokenDevourMode()
  if (mode === 'pounce') return 2
  if (mode === 2 || mode === 3 || mode === 4) return mode
  const w = AWOKEN_DEVOUR.biteWeights
  const total = w[2] + w[3] + w[4]
  const r = rand() * total
  return r < w[2] ? 2 : r < w[2] + w[3] ? 3 : 4
}

/** 主人公の足先 (当たりの半幅) から toeGap 手前に、床へ突っ込んだ頭の前端が落ちる間合い。 */
export function awokenDevourLastBiteGapPx(): number {
  return PLAYER.width / 2 + AWOKEN_DEVOUR.toeGapPx + AWOKEN_DEVOUR.floorPoseFrontPx
    + (awokenDevourJawEnabled() ? AWOKEN_DEVOUR.floorPoseJawExtraFrontPx : 0)
}

/**
 * この回の段取りを決める。startGapPx = いまのボスと主人公の間合い、roomBehindPx = 主人公から見てボスの後ろの壁までの距離
 * (それより遠くへは下がれない)。乱数は Math.random (ボス AI の作法・テストは種で固定する)。
 */
export function planAwokenDevour(startGapPx: number, roomBehindPx: number, rand: () => number = Math.random): AwokenDevourPlan {
  const D = AWOKEN_DEVOUR
  const bites = pickBites(rand)
  const biteMs = Array.from({ length: bites }, () => D.biteMs + Math.round((rand() * 2 - 1) * D.biteJitterMs))
  const landingGapPx = AWOKEN_BITE_REST_FORWARD_PX[AWOKEN_DEVOUR_BASE_ID]!
  const room = Math.max(landingGapPx, roomBehindPx)
  const lastBiteGapPx = Math.min(awokenDevourLastBiteGapPx(), room)
  const ideal = Math.min(room, lastBiteGapPx + bites * D.stepPxByBites[bites])
  let biteStartGapPx: number
  let hopAirMs = 0
  let approachPx = 0
  if (startGapPx < ideal - D.hop.minPx) {
    // 近い: 後ろ跳びで間を取る (宙の長さは跳ぶ距離に合わせて少し延ばす)。
    biteStartGapPx = ideal
    hopAirMs = Math.min(D.hop.maxAirMs, Math.max(D.hop.airMs, D.hop.airMs * (ideal - startGapPx) / D.hop.refPx))
  } else if (startGapPx > ideal + D.approachFreePx) {
    // 遠い: 構えの間に寄る。
    biteStartGapPx = ideal
    approachPx = startGapPx - ideal
  } else {
    // ほぼ合っている: その場から食み始め、噛みの寄りで合わせる (寄りは目安から −20/回数 〜 +30/回数 px)。
    //   1 回 8px に足りない時だけ、構えの間にわずかに下がる (10px 未満)。
    biteStartGapPx = Math.min(room, Math.max(startGapPx, lastBiteGapPx + bites * D.stepMinPx))
    approachPx = biteStartGapPx - startGapPx
  }
  const hopMs = hopAirMs > 0 ? D.hop.crouchMs + hopAirMs + D.hop.landMs : 0
  const stanceMs = D.stanceMs + Math.min(D.approachMaxExtraMs, Math.max(0, Math.abs(approachPx) - D.approachFreePx) * D.approachMsPerPx)
  const biteStartMs = hopMs + stanceMs
  const coilStartMs = biteStartMs + biteMs.reduce((a, b) => a + b, 0)
  const signalMs = coilStartMs + D.coilMs
  const lungeStartMs = signalMs + D.openMs
  const chargeMs = lungeStartMs + D.lungeMs
  return { bites, biteMs, startGapPx, biteStartGapPx, lastBiteGapPx, landingGapPx, hopAirMs, stanceMs,
    biteStartMs, coilStartMs, signalMs, lungeStartMs, chargeMs }
}

/** スイッチが入っている時の覚醒王の致命 = 今の暴噛・終の写しに、この回の段取りと溜めの長さを持たせた物。 */
export function awokenDevourAttack(base: AttackDef, startGapPx: number, roomBehindPx: number, rand: () => number = Math.random,
  startAltitudePx = 0): AttackDef {
  if (awokenDevourMode() === 'pounce') {
    // 跳び噛み2回の版。壁を背にして下がれない時は今の致命のまま (段取りを作らない)。
    //   startAltitudePx = 宙で崩れを見た時の高さ (大暴れ・src/stun_cut.ts)。後ろ跳びの宙の絵から床へ降りる。
    const p = planAwokenPounce(startGapPx, roomBehindPx, startAltitudePx)
    return p ? { ...base, chargeMs: p.chargeMs, devour: p } : base
  }
  const plan = planAwokenDevour(startGapPx, roomBehindPx, rand)
  return { ...base, chargeMs: plan.chargeMs, devour: plan }
}

function easeInOut(t: number): number {
  const p = Math.max(0, Math.min(1, t))
  return p < 0.5 ? 2 * p * p : 1 - 2 * (1 - p) * (1 - p)
}
function easeIn(t: number): number { const p = Math.max(0, Math.min(1, t)); return p * p }

/** 見せの噛み k (0 始まり) の頭の時刻・這い出る長さ・頭を下ろす長さ・顎を閉じる長さ・床を噛む時刻 (灰と音を出す時刻)・終わりの時刻。 */
export function awokenDevourBiteTimes(plan: AwokenDevourPlan): { startMs: number; reachMs: number; downMs: number; closeMs: number; contactMs: number; endMs: number }[] {
  const s = AWOKEN_DEVOUR.biteSplit
  const total = s.reach + s.down + s.close + s.up
  let t = plan.biteStartMs
  return plan.biteMs.map((ms) => {
    const startMs = t
    const reachMs = ms * s.reach / total
    const downMs = ms * s.down / total
    const closeMs = ms * s.close / total
    t += ms
    return { startMs, reachMs, downMs, closeMs, contactMs: startMs + reachMs + downMs, endMs: t }
  })
}

/** 後ろ跳びの弧の高さ (跳ぶ距離が長いほど少し高く)。 */
export function awokenDevourHopHeightPx(plan: AwokenDevourPlan): number {
  const h = AWOKEN_DEVOUR.hop
  const dist = Math.abs(plan.biteStartGapPx - plan.startGapPx)
  return Math.min(h.maxHeightPx, h.heightPx * Math.max(1, dist / h.heightRefPx))
}

/** 溜めの頭から elapsedMs の時の、ボスと主人公の間合い (px) と体の高さ (後ろ跳び・飛び込みの弧)。 */
export function awokenDevourGapAt(plan: AwokenDevourPlan, elapsedMs: number): { gapPx: number; altitudePx: number } {
  if (plan.pounce) return awokenPounceGapAt(plan as AwokenDevourPlan & { pounce: AwokenPounceExtra }, elapsedMs)
  const D = AWOKEN_DEVOUR
  const t = elapsedMs
  // 後ろ跳び
  if (plan.hopAirMs > 0 && t < D.hop.crouchMs + plan.hopAirMs + D.hop.landMs) {
    const p = (t - D.hop.crouchMs) / plan.hopAirMs
    const pc = Math.max(0, Math.min(1, p))
    return { gapPx: plan.startGapPx + (plan.biteStartGapPx - plan.startGapPx) * easeInOut(pc),
      altitudePx: p > 0 && p < 1 ? awokenDevourHopHeightPx(plan) * Math.sin(Math.PI * p) : 0 }
  }
  // 構え (遠い時・わずかに下がる時だけ寄る)
  if (t < plan.biteStartMs) {
    if (plan.hopAirMs > 0) return { gapPx: plan.biteStartGapPx, altitudePx: 0 }
    const stanceStart = plan.biteStartMs - plan.stanceMs
    const p = (t - stanceStart) / plan.stanceMs
    return { gapPx: plan.startGapPx + (plan.biteStartGapPx - plan.startGapPx) * easeInOut(p), altitudePx: 0 }
  }
  // 見せの噛み: 2段の接地 (足を床の上で滑らせない)。体の位置は絵の伸びと組で決まる (awokenDevourBitePose)。
  if (t < plan.coilStartMs) {
    const times = awokenDevourBiteTimes(plan)
    for (let k = 0; k < times.length; k++) {
      if (t < times[k]!.endMs) return { gapPx: awokenDevourBitePose(plan, k, t, 'bite_windup_deep').gapPx, altitudePx: 0 }
    }
  }
  // 本命の溜め: その場 (最後の見せの噛みの間合い)
  if (t < plan.lungeStartMs) return { gapPx: plan.lastBiteGapPx, altitudePx: 0 }
  // 飛び込み: 低い弧で跳んで、噛む間合いへ (後ろ足を床で滑らせない)
  const p = (t - plan.lungeStartMs) / D.lungeMs
  const q = Math.max(0, Math.min(1, p))
  return { gapPx: plan.lastBiteGapPx + (plan.landingGapPx - plan.lastBiteGapPx) * (0.5 * q + 0.5 * easeIn(q)),
    altitudePx: p > 0 && p < 1 ? D.lungeHopPx * Math.sin(Math.PI * Math.min(1, p)) : 0 }
}

// ===== 見せの噛みの足の置き方 (10-09 夜の3回目・2段の接地) =====
/** 表示 186px・横の倍率 1・横の描き位置 0 の時の、絵の中心 (不透明 bbox の中心 = ボスの x) から一番後ろの接地足 (後ろへ +) と
 *  一番前の接地した爪 (前へ +) までの距離 (px)。描く絵 (牙の版) で実測 = tests/awoken_devour_measure.ts。絵を替えたら測り直す
 *  (tests/awoken_devour.test.ts が PNG から測った値と照合する)。 */
export const AWOKEN_DEVOUR_FEET: Readonly<Record<string, { rear: number; front: number }>> = {
  bite_windup_deep: { rear: 111.272, front: 147.159 },
  exec_bite: { rear: 113.898, front: 147.596 },              // exec_bite_fangs_1005
  frenzy_bite2: { rear: 121.009, front: 151.864 },           // frenzy_bite2_fangs_1006
  [AWOKEN_DEVOUR_JAW_IMAGE]: { rear: 121.996, front: 150.648 },
}
const feetOf = (image: string) => AWOKEN_DEVOUR_FEET[image] ?? AWOKEN_DEVOUR_FEET.bite_windup_deep!
const spanOf = (image: string): number => feetOf(image).rear + feetOf(image).front
/** 一口ごとの立ち姿 (噛みの頭と終わり): 頭を上げた構え bite_windup_deep を横 1.02 倍・前へ -6px で描く。 */
const STAND_SX = 1.02, STAND_OFF = -6
/** 噛みしめ (後ろ足を引き寄せる) の拍の区切り (0〜1) と、そこまでに引き寄せた割合・後ろ足の浮き (rearLiftPx に掛ける)。
 *  浮かせてから寄せ始め、寄せ終わってから下ろす。間は直線でつなぐ (絵の動きのキーと体の位置が同じ線をたどる)。 */
/** 這い出る拍の区切り (0〜1) と、そこまでに前足を出した割合・前足の浮き (frontLiftPx に掛ける)。 */
export const AWOKEN_DEVOUR_REACH = {
  fracs: [0, 0.2, 0.5, 0.8, 1],
  done: [0, 0.06, 0.5, 0.94, 1],
  lift: [0, 0.75, 1, 0.75, 0],
} as const
export const AWOKEN_DEVOUR_PULL = {
  fracs: [0, 0.2, 0.45, 0.7, 0.85, 1],
  done: [0, 0.03, 0.35, 0.8, 0.97, 1],
  lift: [0, 0.75, 1, 0.85, 0.5, 0],
  /** 頭を上げた構えへ絵を戻す区切り (今までと同じ・噛みしめの 7 割の所)。 */
  switchAt: 0.7,
} as const
function pullCurve(table: readonly number[], f: number, fr: readonly number[] = AWOKEN_DEVOUR_PULL.fracs): number {
  const x = Math.max(0, Math.min(1, f))
  for (let i = 1; i < fr.length; i++) {
    if (x <= fr[i]!) return table[i - 1]! + (table[i]! - table[i - 1]!) * (x - fr[i - 1]!) / (fr[i]! - fr[i - 1]!)
  }
  return table[table.length - 1]!
}

export type AwokenDevourBitePose = {
  /** ボスと主人公の間合い (= 体の位置)・この絵の横の倍率・前への描き位置・縦の描き位置の足し分・傾き (度)。 */
  gapPx: number
  sx: number
  off: number
  liftOffY: number
  rotDeg: number
}

/**
 * 見せの噛み k の、時刻 t での体の位置と絵の置き方 (image = その時に描く絵)。主人公から見た間合いの軸で:
 *   後ろ足 = 間合い + 倍率 × rear − 前への描き位置、前足 = 間合い − 倍率 × front − 前への描き位置。
 * 這い出る: 後ろ足 R を固定して倍率を上げる (体が前へ伸び、前足だけが一口ぶん出る)。
 * 頭を下ろす〜顎を閉じる: 後ろ足 R と前足 F を両方固定 (絵ごとに足の間が違うので、絵ごとの倍率で足の間を揃える)。
 * 噛みしめ: 前足 F を固定して倍率を戻す (後ろ足が一口ぶん寄る)。後ろ足側は浮かせる。
 */
export function awokenDevourBitePose(plan: AwokenDevourPlan, k: number, t: number, image: string): AwokenDevourBitePose {
  const b = awokenDevourBiteTimes(plan)[k]!
  const step = (plan.biteStartGapPx - plan.lastBiteGapPx) / plan.bites
  const gStart = plan.biteStartGapPx - step * k
  const gEnd = gStart - step
  const W = feetOf('bite_windup_deep')
  const R = gStart + STAND_SX * W.rear - STAND_OFF
  const F = gEnd - STAND_SX * W.front - STAND_OFF
  const reachSx = STAND_SX + step / spanOf('bite_windup_deep')
  const gB = R + STAND_OFF - reachSx * W.rear
  const I = feetOf(image)
  const pullStart = b.contactMs + b.closeMs
  if (t < b.startMs + b.reachMs) {
    const fr = (t - b.startMs) / b.reachMs
    const sx = STAND_SX + (reachSx - STAND_SX) * pullCurve(AWOKEN_DEVOUR_REACH.done, fr, AWOKEN_DEVOUR_REACH.fracs)
    // 後ろ足の下を軸に後ろへ傾けて前足を浮かせる (sin θ = 浮き/足の間)。回すと後ろ足が床から (後ろ足までの距離) × sin θ ずれるので、その分だけ絵ごと戻す。
    const sin = AWOKEN_DEVOUR.frontLiftPx * pullCurve(AWOKEN_DEVOUR_REACH.lift, fr, AWOKEN_DEVOUR_REACH.fracs) / (sx * spanOf('bite_windup_deep'))
    return { gapPx: R - sx * W.rear + STAND_OFF, sx, off: STAND_OFF,
      liftOffY: -(sx * W.rear - STAND_OFF) * sin, rotDeg: Math.asin(sin) * 180 / Math.PI }
  }
  if (t < pullStart) {
    const sx = reachSx * spanOf('bite_windup_deep') / spanOf(image)
    return { gapPx: gB, sx, off: gB + sx * I.rear - R, liftOffY: 0, rotDeg: 0 }
  }
  const f = (t - pullStart) / (b.endMs - pullStart)
  const done = pullCurve(AWOKEN_DEVOUR_PULL.done, f)
  const span = reachSx * spanOf('bite_windup_deep') - done * step
  const sx = span / spanOf(image)
  const g = gB + done * (gEnd - gB)
  const off = g - sx * I.front - F
  // 前足の下を軸に前へ傾けて後ろ足側を浮かせる: 傾き θ (sin θ = −浮き/足の間) で回すと前足は床から (前足までの距離) × sin θ ずれるので、
  //   その分だけ絵ごと持ち上げて前足を床に残す (回転の軸 = 絵の中心の床・drawBossSprite と同じ)。
  const sin = -AWOKEN_DEVOUR.rearLiftPx * pullCurve(AWOKEN_DEVOUR_PULL.lift, f) / span
  return { gapPx: g, sx, off, liftOffY: (sx * I.front + off) * sin, rotDeg: Math.asin(sin) * 180 / Math.PI }
}

/** 紫のオーラの強さ (溜めの間だけ・本命の溜めの 200ms 目から)。null = 出さない。 */
export function awokenDevourAuraK(plan: AwokenDevourPlan, telegraphElapsedMs: number): number | null {
  // 跳び噛みの版は合図の比較 (DEV ?gladiusTell) に従う (src/awoken_devour_pounce.ts)。
  if (plan.pounce) return pounceAuraK(plan, telegraphElapsedMs)
  if (telegraphElapsedMs < plan.signalMs) return null
  const p = Math.min(1, (telegraphElapsedMs - plan.signalMs) / Math.max(1, plan.chargeMs - plan.signalMs))
  return 0.35 + 0.65 * p
}

/** 当たりの瞬間 (active) に致命の紫を出すか。跳び噛みの版の合図「無し」だけ出さない。 */
export function awokenDevourActiveAura(plan: AwokenDevourPlan): boolean {
  return plan.pounce ? pounceActiveAura() : true
}

/** この tick で本命の合図 (紫が灯って一瞬止まる時刻) を跨いだか。 */
export function awokenDevourSignalCrossed(plan: AwokenDevourPlan, prevElapsedMs: number, nextElapsedMs: number): boolean {
  // 跳び噛みの版は、合図の比較で purple の時だけ止める (none / light は止めない)。
  if (plan.pounce && !pounceSignalFreezes()) return false
  return prevElapsedMs < plan.signalMs && nextElapsedMs >= plan.signalMs
}

/** この tick で跨いだ「見せの噛みが床を噛んだ瞬間」の番号 (灰と音を出す)。 */
export function awokenDevourClosesCrossed(plan: AwokenDevourPlan, prevElapsedMs: number, nextElapsedMs: number): number[] {
  const out: number[] = []
  if (plan.pounce) return out   // 跳び噛みの版は床を噛まない (着地の音は awokenPounceLandsCrossed)
  awokenDevourBiteTimes(plan).forEach((b, k) => {
    const at = b.contactMs
    if (prevElapsedMs < at && nextElapsedMs >= at) out.push(k)
  })
  return out
}

/** 床へ突っ込んだ頭の口の前方距離 (灰を出す所・frenzy_bite2 の口 100.2px に噛んだ瞬間の変位を掛けた値)。 */
export const AWOKEN_DEVOUR_FLOOR_MOUTH_PX = 100.2 * 1.04 + 4

// ===== 絵の並び (タイムライン) =====
// 使う絵 (全部いまある絵・溜めと噛みの間は牙ありの版へ自動で読み替わる = awoken_fang_cues.ts):
//   後ろ跳び = back_hop_crouch_1001 → back_hop_air_1001 → back_hop_land_1001 (配布版の後ろ跳びの絵そのもの)
//   構え・噛みの間の頭を上げた所 = bite_windup_deep (頭を下げて口を開けた構え)
//   頭を下ろす途中 = exec_bite (頭が床まで落ちて顎が縦に開いた絵)
//   顎を閉じる (床を噛む) = frenzy_bite2 (頭を床に伏せた絵・顎は横に寝る) + 縦に潰す
//     ?devourJaw=1 の時だけ floorbite_closed_1009 (床に頭を突っ込んで顎を閉じた絵・frenzy_bite2_fangs_1006 と後ろ足・前足の位置が同じ) で、縦の潰しは掛けない
//   本命の溜め = extend_coil (後ろ脚の沈み直し) → rush_windup (起き上がる途中・前脚は床に着いたまま胸と頭が上がる) → exec_windup (立ち上がって口を開けて止まる)
//   飛び込み・当たり・戻り = 今の暴噛・終と同じ (exec_windup → exec_bite → 首振り)
//   3枚 (bite_windup_deep・exec_bite・frenzy_bite2) は後ろ脚の位置がほぼ同じ (差 2〜9px・表示 186px で実測) なので、
//   その場で頭だけ上下して見える。
// 起き上がりの2コマの形 (沈み直しの絵を縦に伸ばして胸を起こす → 起き上がる途中の絵を縦に潰した所から入る)。
const RISE_COIL_Y = 1.18, RISE_COIL_ROT = 2.0, RISE_FROM_Y = 0.82, RISE_FROM_ROT = 0
type Motion = Pick<BossPoseKey, 'offset' | 'scale' | 'rot' | 'ease' | 'hold'>
const HOLD = { hold: true } as const

// 絵の並びは devourJaw の有無で変わる (スイッチはテストが途中で切り替えるので別々に持つ)。
const timelineCache = { plain: new WeakMap<AwokenDevourPlan, BossAttackTimeline>(), jaw: new WeakMap<AwokenDevourPlan, BossAttackTimeline>() }

/** この回の段取りの絵の並び。active と recovery は今の暴噛・終のキーをそのまま使う (当たりの印も同じ)。 */
export function awokenDevourTimeline(plan: AwokenDevourPlan, base: BossAttackTimeline): BossAttackTimeline {
  if (plan.pounce) return awokenPounceTimeline(plan as AwokenDevourPlan & { pounce: AwokenPounceExtra }, base)
  const cacheOf = awokenDevourJawEnabled() ? timelineCache.jaw : timelineCache.plain
  const cached = cacheOf.get(plan)
  if (cached) return cached
  const D = AWOKEN_DEVOUR
  const keys: BossPoseKey[] = []
  const at = (ms: number) => Math.max(0, Math.min(0.999999, ms / plan.chargeMs))
  const key = (ms: number, image: string, m: Motion = {}) => keys.push({ phase: 'telegraph', at: at(ms), image, ...HOLD, ...m })

  // 絵ごとの横の描き位置 (前へ+ px)。絵によって後ろ足の描かれた位置が違うので、切り替わりで着いた足が跳ばないように合わせた値
  //   (表示 186px での実測・docs/research/2026-10-09-awoken-devour-proxy-status.md)。同じ絵の間は変えない (変えると足が滑る)。
  //   extend_coil は後ろ脚を体の下へ寄せた絵で、前後の絵と後ろ足が 35px 違う。体ごと 30px 後ろへ描いて、入る時の跳びを減らし、
  //   出る時 (立ち上がる exec_windup) の後ろ足とほぼ揃える。
  //   rush_windup (起き上がる途中) は後ろ足が絵の中心から近いので、体ごと 20px 後ろへ描いて揃える。
  //   見せの噛みの間の値は awokenDevourBitePose が体の位置と組で決める (構えの W だけここ)。
  const W = STAND_OFF, C = -30, R = -20
  // 顎を閉じる拍の絵。devourJaw=1 なら顎を閉じた新しい絵 (縦の潰しは外す・足の置き方は AWOKEN_DEVOUR_FEET の値で揃える)。
  const useJaw = awokenDevourJawEnabled()
  const closeImage = useJaw ? AWOKEN_DEVOUR_JAW_IMAGE : 'frenzy_bite2'
  const squash = (y: number) => (useJaw ? 1 : y)
  let t = 0
  if (plan.hopAirMs > 0) {
    key(0, 'back_hop_crouch_1001', { ease: 'easeIn' })
    key(D.hop.crouchMs, 'back_hop_air_1001', { ease: 'easeInOut' })
    t = D.hop.crouchMs + plan.hopAirMs
    key(t, 'back_hop_land_1001', { offset: { x: 8, y: 3 }, scale: { x: 1.03, y: 0.96 }, ease: 'step' })
    t += D.hop.landMs
  }
  // 構え: 肩を落として頭を下げる (跳ばなかった時は待機の絵から入る)。
  if (plan.hopAirMs === 0) key(t, 'idle', { ease: 'easeIn' })
  key(t + Math.min(110, plan.stanceMs * 0.4), 'bite_windup_deep', { offset: { x: W, y: 3 }, scale: { x: 1.02, y: 0.97 }, ease: 'easeInOut' })
  key(plan.biteStartMs - 40, 'bite_windup_deep', { offset: { x: W, y: 4 }, scale: { x: STAND_SX, y: 0.96 }, rot: 0.6, ease: 'easeInOut' })

  // 見せの噛み (2段の接地・awokenDevourBitePose)。体の位置と絵の倍率・描き位置は組で動くので、着いた足は床の上で動かない。
  //   区切りごとにキーを置き、間は直線でつなぐ (体の位置も同じ区切りの直線をたどる)。
  const P = AWOKEN_DEVOUR_PULL
  awokenDevourBiteTimes(plan).forEach((b, k) => {
    const pose = (ms: number, image: string, y: number, sy: number, ease: Motion['ease']): void => {
      const p = awokenDevourBitePose(plan, k, ms, image)
      key(ms, image, { offset: { x: p.off, y: y + p.liftOffY }, scale: { x: p.sx, y: sy }, rot: p.rotDeg, ease })
    }
    // 這い出る: 頭を上げたまま、後ろ足を床に置いて体を前へ伸ばす (前足を浮かせて一口ぶん出す)。
    for (const f of AWOKEN_DEVOUR_REACH.fracs) {
      pose(b.startMs + b.reachMs * f, 'bite_windup_deep', 2 + f, 0.98 - 0.01 * f, f < 1 ? 'linear' : 'step')
    }
    // 頭を床へ下ろす → 床を噛んだ瞬間: 縦に潰す (顎が床に食い込む)。devourJaw=1 は顎を閉じた絵なので潰さない。足はどちらも動かない。
    pose(b.startMs + b.reachMs + b.downMs * 0.45, 'exec_bite', 1, 0.99, 'step')
    pose(b.contactMs, closeImage, 2, squash(0.94), 'easeOut')
    // 噛みしめ: 前足を床に置いたまま、後ろ足側を浮かせて一口ぶん引き寄せる (体の伸びを戻す)。途中で頭を上げた構えへ戻す。
    const pullStart = b.contactMs + b.closeMs
    const at = (f: number) => pullStart + (b.endMs - pullStart) * f
    pose(pullStart, closeImage, 1, squash(0.97), 'linear')
    for (const f of P.fracs) if (f > 0 && f < P.switchAt) pose(at(f), closeImage, 1, squash(0.97), 'linear')
    pose(at(P.switchAt) - 1, closeImage, 1, squash(0.97), 'step')
    for (const f of P.fracs) if (f >= P.switchAt && f < 1) pose(at(f), 'bite_windup_deep', 1, 0.99, 'linear')
    // 終わり: 立ち姿 (次の一口の頭と同じ値)。最後の一口の後は沈み直しの絵へ向けて動かさない。
    pose(b.endMs - 1, 'bite_windup_deep', 1, 0.99, 'step')
  })

  // 本命の溜め: 後ろ脚が沈み直す (暴連噛・伸びの extend_coil と同じ動き) → 胸を起こし始める (同じ絵を縦に伸ばす)
  //   → 起き上がる途中の絵 (rush_windup・前脚は床に着いたまま胸と頭が上がる) → 200ms 目で立ち上がり切る (exec_windup)。
  //   絵ごとの横の描き位置 (C・R・-6) は、一番後ろの接地足がどの絵でも同じ所に立つ値 (tests/awoken_devour.test.ts が実測で照合)。
  const c0 = plan.coilStartMs
  key(c0, 'extend_coil', { offset: { x: C, y: 4 }, scale: { x: 1.05, y: 0.93 }, rot: -0.5, ease: 'easeOut' })
  key(c0 + D.coilLowMs, 'extend_coil', { offset: { x: C, y: 6 }, scale: { x: 1.07, y: 0.91 }, rot: 0.4, ease: 'easeInOut' })
  key(c0 + D.riseStartMs - 1, 'extend_coil', { offset: { x: C, y: 0 }, scale: { x: 1.03, y: RISE_COIL_Y }, rot: RISE_COIL_ROT, ease: 'step' })
  key(c0 + D.riseStartMs, 'rush_windup', { offset: { x: R, y: 0 }, scale: { x: 1.0, y: RISE_FROM_Y }, rot: RISE_FROM_ROT, ease: 'easeInOut' })
  key(c0 + D.riseFullMs, 'rush_windup', { offset: { x: R, y: 0 }, scale: { x: 0.99, y: 1.0 }, rot: 0, ease: 'step' })
  // 立ち上がり切って口を開け、首を引く (今の暴噛・終の構えと同じ絵・同じ引き)。ここで紫が灯って一瞬止まる。最後の stillMs は完全に止まる。
  key(plan.signalMs, 'exec_windup', { offset: { x: -6 }, scale: { x: 0.97, y: 1.03 }, rot: -1.0, ease: 'easeOut' })
  key(plan.lungeStartMs - D.stillMs, 'exec_windup', { offset: { x: -11 }, scale: { x: 0.96, y: 1.04 }, rot: -1.5, ease: 'step' })
  // 飛び込み: 構えの絵のまま前へ倒れ込み、当たりの tick で噛みの絵へ (前脚で床を打つ)。
  key(plan.lungeStartMs, 'exec_windup', { offset: { x: -11 }, scale: { x: 0.96, y: 1.04 }, rot: -1.5, ease: 'easeIn' })

  const out = [...keys, ...base.filter((k) => k.phase !== 'telegraph')]
  cacheOf.set(plan, out)
  return out
}
